/**
 * The sentence a dashboard event reads as. Lives in services, not in the
 * component, so non-UI callers (the /looker attention box) can use it without
 * pulling react-router and the icon set into the service graph.
 *
 * @module services/dashboard/dashboardEventText
 */

import type { DashboardEvent } from "./dashboardEvents";

type TFn = (key: string, options?: Record<string, unknown>) => string;

/**
 * The event's sentence, plus the placement count when one creative stands for
 * several ad sets. Appended rather than folded into every message string: the
 * count is a fact ABOUT the row, and duplicating seven translation keys to
 * carry it would guarantee they drift.
 */
export const eventText = (e: DashboardEvent, t: TFn): string => {
  const placements = Number(e.data.placements ?? 0);
  const suffix = placements > 1 ? ` · ${t("home.evPlacements", { count: placements })}` : "";
  return `${sentence(e, t)}${suffix}`;
};

const sentence = (e: DashboardEvent, t: TFn): string => {
  const d = e.data;
  switch (e.kind) {
    case "noConv":
      return t("home.evNoConv", d);
    case "cpaJump":
      return t("home.evCpaJump", {
        ...d,
        prior: Number(d.prior).toLocaleString(),
        recent: Number(d.recent).toLocaleString(),
      });
    case "unmeasurable":
      return t("home.evUnmeasurable", { ...d, spend: Number(d.spend).toLocaleString() });
    case "syncStale":
      return t("home.evSyncStale", { ...d, platforms: (d.platforms as string[]).join(" / ") });
    case "syncFailed":
      return t("home.evSyncFailed", { ...d, error: String(d.error).slice(0, 60) });
    case "stopped":
      return t("home.evStopped", d);
    case "accumulating":
      return t("home.evAccumulating", d);
  }
};
